import { useCallback } from "react";
import { create } from "zustand";

import {
  ConfirmActionDialog,
  type ConfirmActionDialogProps,
} from "../ConfirmActionDialog";

type Event = Pick<
  ConfirmActionDialogProps,
  | "title"
  | "description"
  | "okLabel"
  | "okVariant"
  | "cancelLabel"
  | "cancelVariant"
>;

/**
 * グローバルな ConfirmDialog を呼び出すためのカスタムフック
 */
export function useGlobalConfirmDialog() {
  const state = useGlobalConfirmDialogState();

  return {
    confirmDialog: useCallback(
      async (event: Event): Promise<boolean> => {
        if (state.isOpen) {
          throw new Error("GlobalConfirmDialog is already open");
        }
        return new Promise((resolve) => {
          state.openDialog(event, (isOk) => resolve(isOk));
        });
      },
      [state],
    ),
  };
}

/**
 * グローバルな ConfirmDialog のコンポーネント
 * root に一つだけ配置する想定
 */
export function GlobalConfirmDialog() {
  const state = useGlobalConfirmDialogState();

  return (
    <ConfirmActionDialog
      isOpen={state.isOpen}
      onClose={() => {
        state.callback?.(false);
        state.closeDialog();
      }}
      onConfirm={() => {
        state.callback?.(true);
        state.closeDialog();
      }}
      title={state.event.title}
      description={state.event.description}
      okLabel={state.event.okLabel}
      okVariant={state.event.okVariant}
      cancelLabel={state.event.cancelLabel}
      cancelVariant={state.event.cancelVariant}
    />
  );
}

/**
 * GlobalConfirmDialog 用の状態
 */
type Callback = (isOk: boolean) => void;

type State = {
  event: Event;
  isOpen: boolean;
  callback?: Callback;
  openDialog: (props: Event, callback?: Callback) => void;
  closeDialog: () => void;
};

const useGlobalConfirmDialogState = create<State>((set) => ({
  event: {
    title: "",
    description: "",
  },
  isOpen: false,
  openDialog: (event: Event, callback?: Callback) =>
    set({ event, isOpen: true, callback }),
  closeDialog: () =>
    set((prev) => ({ ...prev, isOpen: false, callback: undefined })),
}));
